// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// this should handle the qrCode printing functionality
// each blueprint has its own styling and will be applying the style changes here

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import siteMessage from "@insite/client-framework/SiteMessage";

export const printItems = (
    selector: string,
    line: any,
    productInfo: ProductInfo[],
    single?: boolean,
    isProduct?: boolean,
) => {
    let iframeContent = "";
    const iframe = window.frames[0];
    const fontFamily = fontConfig.fontFamily;
    const websiteUrl = siteMessage("Custom_WebsiteUrl");

    const topMargin = BLUEPRINT_NAME === "bp" ? 120 : 130;
    const cardWidth = BLUEPRINT_NAME === "bp" ? 325 : 340;
    const leftMargin = BLUEPRINT_NAME === "bp" ? 60 : 45;

    if (single) {
        const prtContent = document.querySelector(selector) as HTMLCanvasElement;
        const img = prtContent.toDataURL("image/png");
        const productDescription = isProduct ? line.productTitle : line.shortDescription;
        const productSku = isProduct ? line.detail.sku || line.productNumber : line.properties.sku || line.erpNumber;
        const itemPrice = productInfo[0]?.pricing?.unitNetPriceDisplay || "N/A";
        const uom = productInfo[0]?.unitOfMeasure;

        const itemContent = buildQrCodeStructure(img, productDescription, productSku, itemPrice, uom, websiteUrl);
        const repeatedContent = `<div class="print-item">${itemContent}</div>`;

        iframeContent = `
        <head>
            <style>
                body {
                    margin: 0;
                    padding: 0;
                }
                @page { margin-top: ${topMargin}px; }
                @page:first {
                    margin-top: ${topMargin}px;
                }
                .print-item { 
                    width:340px; 
                    height:150px; 
                    display: inline-block;
                    margin-bottom: 66px; 
                    margin-left: ${BLUEPRINT_NAME === "bp" ? "2px" : "10px"};
                }
                .print-item:nth-child(even) {
                    margin-left: 45px; 
                }
            </style>
        </head>
        <body onload="window.print()" style="font-family: ${fontFamily};">
            ${repeatedContent}
        </body>`;
    } else {
        let itemsContent = "";
        const prtContent = document.querySelectorAll(selector);
        prtContent.forEach((canvas, index) => {
            const img = (canvas as HTMLCanvasElement).toDataURL("image/png");
            const productDescription = line[index].shortDescription || line[index].productTitle;
            const productSku = isProduct
                ? line[index].detail.sku || line[index].productNumber
                : line[index].properties.sku || line[index]?.erpNumber;
            const price =
                productInfo[line[index].id]?.pricing?.unitNetPriceDisplay ||
                productInfo[index]?.pricing?.unitNetPriceDisplay ||
                "N/A";
            const uom = productInfo[line[index].id]?.unitOfMeasure || productInfo[index]?.unitOfMeasure;

            itemsContent += `<div class="print-item">
                ${buildQrCodeStructure(img, productDescription, productSku, price, uom, websiteUrl)}
            </div>`;
        });

        iframeContent = `
         <head>
            <style>
                body {
                    padding: 0;
                    font-size: 0;
                    width: 725px;
                    margin: 0 auto;                
                }
                @page { margin-top: ${topMargin}px; }
                @page:first {
                    margin-top: ${topMargin}px;
                }
                .print-item { 
                    width:${cardWidth}px; 
                    height:150px; 
                    vertical-align: middle;
                    margin-bottom: 66px; 
                }
                .print-item:nth-child(even) {
                    margin-left: ${leftMargin}px; 
                }
            </style>
        </head>
        <body onload="window.print()" style="font-family: ${fontFamily};">
            <div style="display: flex; flex-wrap: wrap;">
                ${itemsContent}
            </div>
        </body>`;
    }

    iframe.document?.write(iframeContent);
    iframe.document?.close();
};

const buildQrCodeStructure = (
    img: any,
    description: string,
    sku: string,
    price: string,
    uom: string,
    websiteUrl: any,
) => {
    const hidePrice = true;

    switch (BLUEPRINT_NAME) {
        case "bg":
            return `
            <div style="width: 100%; page-break-inside: avoid; overflow: hidden; display: flex; align-items:center;">
                <div style="display: flex;align-items:center; page-break-inside: avoid; margin-right: 10px;">
                    <span style="transform: rotate(180deg); writing-mode: vertical-lr; text-orientation: sideways-right; margin-bottom: 5px; font-size:10px;text-align: center;">${websiteUrl}</span>    
                    <img src="${img}" width="150px" height="150px" />
                </div>
                <div style="flex-grow: 1; display: flex;flex-direction: column; justify-content: space-between; height: 130px;">
                    <span style="display: block; font-size: 14px; margin-bottom: 5px; width: 100%; word-break: break-word; line-height: 16px; height: 32px; overflow: hidden;">${description}</span>
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                        <div>
                        ${
                            !hidePrice
                                ? `<span style="display: block;margin-bototm: 10px;font-size: 14px;"><strong>Price:</strong> ${
                                      price || "N/A"
                                  } ${uom ? `/ ${uom}` : ""}</span>`
                                : ""
                        }
                            <span style="display: block; font-size: 14px;"><strong>Sku:</strong> ${sku}</span>
                        </div>
                        <div style="width: 40px; height: 40px;">${BgSvg}</div>
                    </div>
                </div>            
            </div>`;

        case "bgs":
            return `
            <div style="width: 100%; page-break-inside: avoid; overflow: hidden; display: flex; align-items:center;">
                <div style="display: flex;align-items:center; page-break-inside: avoid; margin-right: 5px;">
                    <span style="transform: rotate(180deg); writing-mode: vertical-lr; text-orientation: sideways-right; margin-bottom: 5px; font-size:10px;text-align: center;">${websiteUrl}</span>    
                    <img src="${img}" width="150px" height="150px" style="page-break-inside: avoid;" />
                </div>
                <div style="flex-grow: 1; display: flex;flex-direction: column; justify-content: space-between; height: 130px;">
                    <span style="display: block; font-size: 14px; margin-bottom: 5px; width: 100%; word-break: break-word; line-height: 16px; height: 32px; overflow: hidden;">${description}</span>
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                        <div style="width: 40px; height: 40px; margin-right: 5px;">${BgsSvg}</div>
                        <div>
                         ${
                             !hidePrice
                                 ? `
                            <span style="display: block;margin-bototm: 10px;font-size: 14px;"><strong>Price:</strong> ${
                                price || "N/A"
                            } ${uom ? `/ ${uom}` : ""}</span>`
                                 : ""
                         }
                            <span style="display: block; font-size: 14px;"><strong>Sku:</strong> ${sku}</span>
                        </div>
                    </div>
                </div>            
            </div>`;

        case "bp":
            return `
            <div style="width: 100%; display: flex; justify-content: flex-start;break-inside: avoid;">     
                <div style="width: 160px; page-break-inside: avoid; margin-right: 10px;">             
                    <img src="${img}" width="150px" height="150px" style="break-inside: avoid;" />  
                    <span style="padding-left: 8px; font-weight: bold; font-size: 12px;">${websiteUrl}</span>           
                </div>                           
                <div style="flex-grow: 1; display: flex; flex-direction: column; justify-content: space-between; page-break-inside: avoid;">
                    <div style="width: 100%; text-align: right;">${BpSvg}</div>
                    <span style="font-size: 14px; line-height: 16px; height: 32px; overflow: hidden;">${description}</span>     
                    <div style="text-align: right;">
                    ${
                        !hidePrice
                            ? `
                        <span style="display: block; font-size: 14px;"><strong>Price:</strong> ${price || "N/A"} ${
                            uom ? `/ ${uom}` : ""
                        }</span>`
                            : ""
                    }
                        <span style="display: block; font-size: 14px;"><strong>Sku:</strong> ${sku}</span>
                    </div>                                                 
                </div>                                                                             
            </div>
            `;
    }
};

const BgsSvg = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
width="40px" height="40px" viewBox="0 0 134.19 134.19" enable-background="new 0 0 134.19 134.19" xml:space="preserve">
<g>
<path fill="#231F20" d="M35.17,63.36H19.18V45.68h16.37c7.44,0,11.71,2.98,11.71,8.19v0.15c0,2.93-1.09,5.24-3.23,6.84
   C41.88,62.47,38.74,63.36,35.17,63.36z"/>
<path fill="#231F20" d="M50.24,79.1v0.16c0,5.8-4.98,9.26-13.31,9.26H19.18V70.38h16.45C42.29,70.38,50.24,71.89,50.24,79.1z"/>
<path fill="#231F20" d="M86.61,64.43H66.46v7.02h17.89v12.89c-1.77,1.37-4.02,2.6-6.43,3.53c-2.85,1.13-5.95,1.72-8.95,1.72
   c-4.94,0-9.21-1.29-12.73-3.84c0.93-1.97,1.4-4.15,1.4-6.49V79.1c0-3.8-1.38-7.06-4.1-9.69c-1.44-1.38-3.23-2.52-5.43-3.44
   c1.27-0.84,2.35-1.78,3.25-2.84c2.22-2.61,3.3-5.71,3.3-9.49v-0.15c0-1.1-0.11-2.15-0.34-3.15c3.67-3.7,8.43-5.66,13.81-5.66
   c7.18,0,11.47,2.32,14.64,4.8c-0.42,1.36-0.63,2.75-0.63,4.16v0.15C82.14,58.38,83.56,61.77,86.61,64.43z"/>
<path fill="#231F20" d="M115.33,80.25v0.15c0,2.51-1.08,4.7-3.1,6.33c-2.19,1.7-5.31,2.63-8.77,2.63c-3.56,0-6.64-0.57-9.66-1.78
   c-0.75-0.31-1.5-0.69-2.28-1.14V67.47c2.68,1.21,6,2.22,10.01,3.03c5.56,1.11,9.2,2.44,11.44,4.17
   C114.69,76.05,115.33,77.56,115.33,80.25z"/>
<path fill="#231F20" d="M0,0v134.19h134.19V0H0z M122.65,80.02c0,4.6-2.03,8.92-5.58,11.84c-3.56,2.87-8.47,4.44-13.83,4.44
   c-6.46,0-11.93-1.52-17.11-4.77c-3.91,2.37-9.85,4.92-17.31,4.92c-6.57,0-12.47-1.79-17.17-5.19c-3.74,2.79-8.89,4.28-14.88,4.28
   H11.85V38.66h23.78c6.36,0,11.72,1.86,15.1,5.23c0.02,0.02,0.04,0.04,0.06,0.06c4.87-4.07,10.91-6.21,17.57-6.21
   c4.57,0,8.29,0.62,11.7,1.95c2.19,0.88,4.33,2.07,6.5,3.62c0.35-0.35,0.71-0.67,1.08-0.98c3.52-2.9,8.14-4.44,13.38-4.44
   c7.8,0,13.43,2,19.41,6.88l1.28,1.05l-4.65,5.78l-1.31-1.13c-4.56-3.95-9.01-5.64-14.89-5.64c-3.38,0-6.29,0.91-8.43,2.62
   c-1.95,1.51-2.97,3.51-2.97,5.81v0.15c0,2.71,0.66,4.3,2.35,5.65c2.24,1.8,6.02,3.2,11.91,4.41h0.01
   c13.26,2.77,18.92,7.67,18.92,16.4V80.02z"/>
</g>
</svg>
`;

const BgSvg = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
width="40px" height="40px" viewBox="0 0 86.283 85.127" enable-background="new 0 0 86.283 85.127" xml:space="preserve">
<g>
<path fill="#231F20" d="M82.636,57.056c0,1.856-0.163,3.643-0.504,5.355c-0.329,1.718-0.819,3.331-1.458,4.849
   c-0.649,1.517-1.423,2.911-2.313,4.19c-0.905,1.278-1.917,2.437-3.063,3.471c-2.282,2.055-4.846,3.596-7.685,4.615
   c-2.853,1.015-5.816,1.522-8.892,1.522c-4.818,0-9.265-1.294-13.292-3.864c-4.065-2.598-6.973-6.28-8.696-10.987L36.48,65.5h12.886
   l0.162,0.21c0.813,1.105,2.102,2.035,3.898,2.779c0.886,0.364,1.805,0.646,2.747,0.839c0.944,0.188,1.894,0.282,2.851,0.282
   c3.474,0,6.429-1.034,8.942-3.109c1.528-1.262,2.632-2.96,3.321-5.116c-1.467,0.831-3.007,1.484-4.621,1.932
   c-1.946,0.55-4.865,1.222-8.098,1.222c-1.863,0-3.695-0.251-5.491-0.767c-1.791-0.509-3.548-1.272-5.251-2.284
   c-1.731-1.021-3.295-2.26-4.692-3.711c-1.401-1.459-2.631-3.112-3.696-4.975c-2.029-3.543-3.118-7.537-3.225-11.95
   c-0.006-0.035-0.012-0.151-0.012-0.151l0.154-36.355h11.333v16.202c0.224-0.118,0.44-0.248,0.668-0.36
   c1.69-0.845,3.509-1.595,5.229-2.023c2.291-0.568,4.702-0.978,7.048-0.973c3.111,0,6.111,0.668,8.978,2.003
   c2.76,1.273,5.224,3.041,7.214,5.349c3.527,4.108,5.828,9.293,5.826,14.754L82.636,57.056z M29.418,55.681
   c0,0,1.055-7.715,1.099-7.674c0.04-1.274-2.328-0.765-2.328-0.765l-10.282,1.003l0.043-0.183c0,0,1.17-3.475,1.17-3.44
   c0.621-0.906-0.586-1.776-0.586-1.776L4.003,32.483c0,0,3.007-1.159,3.047-1.198c1.211-0.181,0.794-1.416,0.794-1.416l-2.55-8.117
   c0,0,7.895,1.381,7.895,1.344c1.628,0.541,2.04-0.651,2.04-0.651l1.01-3.297l6.758,6.485c2.804,1.958,2.301-0.833,2.301-0.833
   l-2.843-13.628c0,0,4.093,1.744,4.012,1.704c1.611,0.728,2.209,0.197,2.413-0.127c0.069-0.111,0.092-0.2,0.092-0.2l4.764-7.489
   l0.427-0.711l0.009,51.338L29.418,55.681z M75.517,18.034c-1.417-1.069-2.962-1.994-4.61-2.784
   c-6.645-2.816-11.896-2.647-18.479-1.143c-0.21,0.055-0.42,0.123-0.629,0.183V0H32.301l-6.5,11.146
   c0.087,0.042-6.386-2.842-6.386-2.842l-1.655,2.885l2.482,10.306c0,0-2.625-3.074-5.577-5.153l-3.063,3.357l-8.501-0.673L0,21.494
   c0,0.038,4.005,9.654,4.005,9.654L0.38,32.638l0.655,5.348c-0.047,0.036,15.902,9.065,15.902,9.065l-1.15,4.263l1.508,2.848
   c0-0.038,8.375-1.007,8.375-1.007l-1.338,5.687h5.573c0.342,1.32,1.915,7.057,4.815,12.341c0.127,0.238,0.26,0.467,0.396,0.705
   c0.145,0.248,0.289,0.492,0.438,0.735c0.179,0.292,0.358,0.582,0.548,0.864c1.337,2.054,2.898,3.896,4.691,5.221
   c0.787,0.664,1.606,1.298,2.481,1.863c4.564,2.995,9.801,4.554,15.267,4.554h0.072c3.481,0,6.902-0.596,10.154-1.773
   c3.317-1.211,6.333-3.061,8.953-5.48c1.361-1.252,2.564-2.656,3.613-4.184c1.043-1.52,1.938-3.164,2.682-4.918
   c0.757-1.822,1.317-3.717,1.688-5.695c0.376-1.99,0.559-4.039,0.559-6.134l0.021-17.763C86.121,30.983,82.087,22.988,75.517,18.034
   z"/>
<path fill="#231F20" d="M71.041,37.285c-0.222-0.813-0.534-1.612-0.929-2.388c-0.133-0.238-0.278-0.473-0.427-0.701
   c-0.052-0.074-0.098-0.156-0.148-0.23c-2.145-3.127-5.741-5.182-9.821-5.189c-6.569,0.015-11.881,5.312-11.891,11.87
   c0.01,6.555,5.322,11.854,11.891,11.871c4.642-0.011,8.65-2.671,10.613-6.542c0.748-1.692,1.13-3.534,1.13-5.549
   C71.459,39.351,71.317,38.308,71.041,37.285z M58.172,48.226c-3.907,0-7.077-3.375-7.077-7.541c0-4.165,3.17-7.541,7.077-7.541
   c3.91,0,7.077,3.376,7.077,7.541C65.249,44.85,62.083,48.226,58.172,48.226z"/>
</g>
</svg>
`;

const BpSvg = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
width="40px" height="40px" viewBox="0 0 108.25 108.25" enable-background="new 0 0 108.25 108.25" xml:space="preserve">
<g>
<path fill="#231F20" d="M52.52,93.22c0.28,0.24,0.44,0.59,0.44,0.96c0,0.39-0.18,0.76-0.49,0.99c-0.18,0.14-0.4,0.23-0.62,0.25
   c-0.5,0.09-1.01,0.13-1.52,0.13c-2.61,0-5.34-1.06-7.43-2.92c-2.82-2.52-4.22-6.2-3.95-10.36c0.04-0.6,0.5-1.08,1.1-1.15
   c0.59-0.07,1.16,0.29,1.34,0.87c0.03,0.08,2.83,8.84,10.57,10.96C52.16,93,52.35,93.09,52.52,93.22z"/>
<path fill="#231F20" d="M54.12,0C24.23,0,0,24.23,0,54.12c0,29.9,24.23,54.13,54.12,54.13c29.9,0,54.13-24.23,54.13-54.13
   C108.25,24.23,84.02,0,54.12,0z M61.86,35.88l13.25-16.22c0.84-1.03,2.35-1.18,3.38-0.34c1.02,0.84,1.18,2.35,0.34,3.37
   L65.58,38.92c-0.48,0.58-1.17,0.88-1.86,0.88c-0.54,0-1.07-0.17-1.52-0.54C61.17,38.42,61.02,36.91,61.86,35.88z M53.92,8.7
   c0.66,0,1.26,0.27,1.7,0.71c0.43,0.43,0.7,1.03,0.7,1.69l0.01,20.95c0,1.33-1.08,2.4-2.4,2.4c-1.33,0-2.4-1.07-2.4-2.4L51.52,11.1
   C51.52,9.78,52.6,8.7,53.92,8.7z M29.76,19.32c1.03-0.84,2.54-0.69,3.38,0.34l13.25,16.22c0.84,1.03,0.68,2.54-0.34,3.38
   c-0.45,0.37-0.99,0.54-1.52,0.54c-0.7,0-1.39-0.3-1.86-0.88L29.42,22.69C28.58,21.67,28.73,20.16,29.76,19.32z M15.9,39.18
   c0.58-1.19,2.03-1.67,3.21-1.09l18.77,9.31c1.19,0.59,1.67,2.03,1.08,3.22c-0.42,0.85-1.27,1.34-2.15,1.34
   c-0.36,0-0.72-0.09-1.06-0.26l-18.77-9.31C15.79,41.8,15.31,40.36,15.9,39.18z M53.71,99.76v0.01h-0.1v-0.01
   C42.82,99.7,30.94,88.55,34.58,73.91c3.66-14.68,16.86-30.44,18.39-32.37c1.53-1.94,3.13,0,3.13,0
   c1.52,1.93,12.98,17.69,16.64,32.37C76.38,88.55,64.5,99.7,53.71,99.76z M91.27,42.39L72.5,51.7c-0.34,0.17-0.71,0.26-1.06,0.26
   c-0.89,0-1.74-0.49-2.16-1.34c-0.59-1.19-0.1-2.63,1.09-3.22l18.76-9.31c1.19-0.59,2.63-0.1,3.22,1.09
   C92.94,40.36,92.46,41.8,91.27,42.39z"/>
</g>
</svg>`;
